import React, { useCallback, useMemo, useState } from 'react';
import { N100 } from '@trello/colors';

import {
  ActionSubjectIdType,
  Analytics,
  SourceType,
} from '@trello/atlassian-analytics';
import { useAnalyticsAttributes } from './AnalyticsAttributesProvider';
import { getColorInitial } from './getColorInitial';
import { ArrowRight } from './icons/ArrowRight';
import { getIcon } from './icons/getIcon';
import styles from './UiNavLink.less';

// If not a reference to a value from @trello/colors, each of these is the
// {key}100 color value from the BXP repo
const ICON_COLORS = {
  B: 'rgba(76, 154, 255, 1)',
  G: 'rgba(121, 242, 192, 1)',
  M: 'rgba(249, 156, 219, 1)',
  N: N100,
  R: 'rgba(255, 143, 115, 1)',
  T: 'rgba(121, 226, 242, 1)',
  Y: 'rgba(255, 227, 128, 1)',
};

// Each of these is the {key}50 color value from the BXP repo @ 0.5 opacity
const BACKGROUND_COLORS = {
  B: 'rgba(222, 235, 255, 0.5)',
  G: 'rgba(227, 252, 239, 0.5)',
  M: 'rgba(255, 242, 251, 0.5)',
  R: 'rgba(255, 235, 229, 0.5)',
  T: 'rgba(230, 252, 255, 0.5)',
  Y: 'rgba(255, 250, 229, 0.5)',
};

interface UiNavLinkProps {
  addBackgroundColorOnHover?: boolean;
  titleHeading: string;
  description: string;
  url: string;
  icon?:
    | 'Bolt'
    | 'Box'
    | 'BrowserCode'
    | 'Building'
    | 'ButlerAutomationHead'
    | 'GlobeNetworkLocation'
    | 'Lego'
    | 'Lightbulb'
    | 'Megaphone'
    | 'New'
    | 'Pencil'
    | 'RocketInclined'
    | 'Star'
    | 'TrelloCardTemplates';
  iconColor?: 'Blue' | 'Green' | 'Magenta' | 'Red' | 'Teal' | 'Yellow';
  decorator?: 'Arrow Right';
  tabIndex?: number;
  analyticsLinkName: ActionSubjectIdType;
  analyticsSource: SourceType;
}

export const UiNavLink: React.FunctionComponent<UiNavLinkProps> = ({
  addBackgroundColorOnHover = true,
  titleHeading,
  description,
  url,
  icon,
  iconColor,
  decorator,
  tabIndex,
  analyticsLinkName,
  analyticsSource,
}) => {
  const analyticsAttributes = useAnalyticsAttributes();
  const [decoratorOnHover, setDecoratorOnHover] = useState(false);

  const onClick = useCallback(
    () =>
      Analytics.sendClickedLinkEvent({
        linkName: analyticsLinkName,
        source: analyticsSource,
        attributes: {
          eventContainer: 'navigation',
          ...analyticsAttributes,
          event: 'clicked',
          eventComponent: 'link',
          label: titleHeading,
          href: url,
          isMarketingEvent: true,
        },
      }),
    [
      analyticsAttributes,
      titleHeading,
      url,
      analyticsLinkName,
      analyticsSource,
    ],
  );

  const onMouseEnter = useCallback(() => setDecoratorOnHover(true), []);
  const onMouseLeave = useCallback(() => setDecoratorOnHover(false), []);

  const backgroundColorPrefix = getColorInitial(
    iconColor,
  ) as keyof typeof BACKGROUND_COLORS;

  const iconColorPrefix = (iconColor?.charAt(0) ||
    'N') as keyof typeof ICON_COLORS;

  const navLinkStyle = useMemo(
    () => ({
      '--marketing-nav-link-hover-background-color':
        BACKGROUND_COLORS[backgroundColorPrefix],
    }),
    [backgroundColorPrefix],
    // Cast directly as this type in order to support custom variable syntax
  ) as React.CSSProperties;

  const navIconStyle = useMemo(
    () => ({
      '--marketing-nav-link-icon-color': ICON_COLORS[iconColorPrefix],
    }),
    [iconColorPrefix],
    // Cast directly as this type in order to support custom variable syntax
  ) as React.CSSProperties;

  return (
    <a
      className={styles.navLink}
      href={url}
      onClick={onClick}
      data-test-id="ui-nav-link"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={addBackgroundColorOnHover ? navLinkStyle : undefined}
      tabIndex={tabIndex}
    >
      <div className={styles.navLinkTitleContainer}>
        {icon && (
          <div className={styles.navIcon} style={navIconStyle}>
            {getIcon(icon)}
          </div>
        )}
        <p className={styles.navLinkTitle}>{titleHeading}</p>
        {decorator && decoratorOnHover && (
          <ArrowRight color="rgba(101, 84, 192, 1)" />
        )}
      </div>
      <div className={styles.navLinkDescription}>
        <p>{description}</p>
      </div>
    </a>
  );
};
